import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  MobileStepper,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {analytics, useNews} from '@resurs-internal/rb-app-api';
import React, {useRef} from 'react';
import {Swiper, SwiperClass, SwiperSlide} from 'swiper/react';
import {FontIcon} from '@resurs-internal/mui';
import {useDialog, useLocalState, useTranslate} from '../../../hooks';
import {NewsStep} from './NewsStep';
import 'swiper/css';

export const NewsDialog: React.FC = () => {
  const [serializedSkipIds, setSerializedSkipIds] = useLocalState(
    `skip-news-ids`,
    '[]',
  );

  const skipIds: string[] = serializedSkipIds
    ? JSON.parse(serializedSkipIds)
    : [];

  const news = useNews({platform: 'WEB', skipIds});

  const steps = news.query.isSuccess ? news.query.data : [];
  const translate = useTranslate();
  const {onClose, open} = useDialog('news');
  const [activeStep, setActiveStep] = React.useState(0);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const swiperRef = useRef<{swiper: SwiperClass}>(null);

  const handleClose = () => {
    analytics.click({name: 'news_close'});
    if (!steps) return;
    const newsIds = steps.map((step) => step.announcementId);
    const skipIds = serializedSkipIds ? JSON.parse(serializedSkipIds) : [];
    const updatedSkipIds = [...new Set([...skipIds, ...newsIds])];
    setSerializedSkipIds(JSON.stringify(updatedSkipIds));
    onClose();
  };

  if (!steps?.length) return null;

  const handleNext = () => {
    if (swiperRef.current && swiperRef.current.swiper) {
      if (activeStep === steps.length - 1) {
        handleClose();
        analytics.click({name: 'news_done'});
      } else {
        setActiveStep((s) => s + 1);
        swiperRef.current.swiper.slideNext();
        analytics.click({name: 'news_next'});
      }
    }
  };

  const handleBack = () => {
    if (swiperRef.current && swiperRef.current.swiper) {
      if (activeStep === 0) {
        handleClose();
        analytics.click({name: 'news_skip'});
      } else {
        swiperRef.current.swiper.slidePrev();
      }
    }
  };

  const handleSwiperChange = (swiper: {activeIndex: number}) => {
    setActiveStep(swiper.activeIndex);
  };

  if (steps.length === 0) return null;
  return (
    <Dialog
      fullScreen={fullScreen}
      fullWidth
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          background: theme.palette.background.paper,
          maxHeight: {xs: '95%', md: '95%'},
          position: {xs: 'absolute', md: 'relative'},
          borderTopRightRadius: {xs: '20px', md: undefined},
          borderTopLeftRadius: {xs: '20px', md: undefined},
          bottom: 0,
          border: 0,
        },
      }}>
      <Box
        sx={{
          position: 'absolute',
          right: 12,
          top: 12,
          zIndex: 99,
        }}>
        <IconButton
          size="small"
          onClick={() => {
            handleClose();
          }}
          sx={(theme) => ({
            background: theme.palette.background.paper,
            ':hover': {background: theme.palette.grey[200]},
          })}>
          <FontIcon icon={'xmark'} />
        </IconButton>
      </Box>
      <DialogContent
        sx={{
          minWidth: {xs: '100%', md: '600px'},
          height: '600px',
          p: 'unset',
          '& .swiper': {
            height: '100%',
          },
        }}>
        <Swiper
          ref={swiperRef}
          onSlideChange={handleSwiperChange}
          spaceBetween={0}
          slidesPerView={1}>
          {steps.map((step, index) => (
            <SwiperSlide key={index}>
              <NewsStep step={step} handleClose={handleClose} />
            </SwiperSlide>
          ))}
        </Swiper>
      </DialogContent>
      <MobileStepper
        variant="dots"
        steps={steps?.length}
        position="static"
        activeStep={activeStep}
        sx={{
          pb: 2,
          justifyContent: 'center',
        }}
        backButton={undefined}
        nextButton={undefined}
      />
      <DialogActions
        sx={{
          borderTop: '1px solid',
          borderColor: theme.palette.grey[300],
          justifyContent: {xs: 'center', md: 'end'},
        }}>
        <Button
          sx={{display: {xs: 'none', md: 'block'}}}
          color={'secondary'}
          variant={'text'}
          onClick={handleBack}>
          {activeStep === 0
            ? translate('common.skip')
            : translate('common.back')}
        </Button>

        <Button variant={'text'} onClick={handleNext}>
          {activeStep === steps.length - 1
            ? translate('common.done')
            : translate('common.next')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
